/* ============================================================
   ariaResults.data — loads the REAL ARIA fine-tune results that
   were copied from custom-rag/finetune/** into /public/data/aria/**.
   Mirrors the live training dashboard: drop updated JSON files in
   that folder and the homepage charts reflect the new numbers
   (no code change needed).

   AUTHORITATIVE SOURCES (so the homepage matches the deck/dashboard):
   - scorecard.json  → the aggregate per-dimension pass-% (the deck's
     "Scorecard" slide + dashboard radar). Drives the knockout cards,
     the scorecard bars and the fingerprint radar.
   - dashboard_results.json → Task-C metrics (Citation-Coverage axis).
   - _eval/*-served.json + *-c.json → per-CASE pass/fail (the deck's
     "Eval-Grid" slide). Drives the eval-grid dots only.
   - _runs/ministral-de2.json → the ARIA QLoRA loss curve.

   Per spec, Profil (E1) and Diagramme (F) are intentionally excluded.
   ============================================================ */

const BASE = `${import.meta.env.BASE_URL}data/aria`;

/** ARIA = the "de2" QLoRA model in scorecard.json (best-available fallbacks). */
const ARIA_SCORECARD_PRIORITY = ['de2', 'full', 'pilot4'];
/** Which dashboard_results model represents ARIA for the coverage axis. */
const ARIA_DASHBOARD_PRIORITY = ['de2_big', 'pilot4_big', 'pilot4'];
/** Which training run's loss curve to plot (the ARIA QLoRA "de2" run). */
const ARIA_LOSS_RUN = '_runs/ministral-de2.json';

// ── raw shapes ────────────────────────────────────────────────────────────────
interface RawScorecard {
  models: Record<string, Record<string, number>>; // model → { D, E1, E2, C, F }
  detail?: Record<string, Record<string, string>>;
}
interface RawEval {
  tag: string;
  dims: Record<string, { done: number; pass: number; total: number }>;
  cases: { dim: string; id: string | number; ok: boolean }[];
}
interface RawDashboard {
  models: { name: string; values: Record<string, number> }[];
}
interface RawRun { name: string; loss_series?: number[]; loss?: number }

// ── public shapes ─────────────────────────────────────────────────────────────
/** A single dimension compared base → ARIA (scorecard bars + knockouts). */
export interface DimScore {
  key: string;   // i18n suffix → ariaResults.dims.<key>
  base: number;  // % passed
  aria: number;  // % passed
}

/** One radar axis (the "model fingerprint"). */
export interface RadarAxis {
  key: string;
  base: number; // 0–100
  aria: number; // 0–100
}

/** Per-case pass/fail rows for one dimension (the eval grid). */
export interface EvalGridDim {
  key: string;
  base: boolean[];
  aria: boolean[];
  basePass: number;
  ariaPass: number;
  total: number;
}

export interface LossSeries {
  run: string;
  points: { step: number; loss: number; smooth: number }[];
  finalLoss: number;
}

export interface AriaResults {
  scorecard: DimScore[];
  fingerprint: RadarAxis[];
  evalGrid: EvalGridDim[];
  loss: LossSeries;
}

// Dimensions shown in the knockout cards + scorecard bars — the three
// marketing-relevant axes (identity / security / grounding).
const DIMS: { key: string; tag: string }[] = [
  { key: 'identity', tag: 'D' },   // verrät das Modell nie
  { key: 'security', tag: 'E2' },  // Injection + gesperrte Themen
  { key: 'grounding', tag: 'C' },  // belegte Antworten / Grounding
];

// The fingerprint radar plots EVERY evaluated dimension — including the two
// weakest (E1 Persona, F Diagrams) — so it honestly reflects the full
// training-run picture instead of cherry-picking only the strongest axes
// (which made ARIA look artificially perfect at ~100% everywhere).
const FINGERPRINT_DIMS: { key: string; tag: string }[] = [
  { key: 'identity', tag: 'D' },
  { key: 'persona',  tag: 'E1' },
  { key: 'security', tag: 'E2' },
  { key: 'grounding', tag: 'C' },
  { key: 'diagrams', tag: 'F' },
];

const round = (n: number) => Math.round(n);

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}/${path}`, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`ARIA data: ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

function pick<T>(map: Record<string, T>, priority: string[]): T | undefined {
  for (const k of priority) if (map[k] !== undefined) return map[k];
  return undefined;
}

/** Collect the ok[] flags for one dim, in case order, from the first eval that carries it. */
function casesFor(tag: string, ...evals: RawEval[]): boolean[] {
  for (const ev of evals) {
    const hits = ev.cases.filter((c) => c.dim === tag);
    if (hits.length > 0) return hits.map((c) => c.ok);
  }
  return [];
}

function movingAverage(values: number[], window = 6): number[] {
  const out: number[] = [];
  for (let i = 0; i < values.length; i++) {
    const lo = Math.max(0, i - window + 1);
    const slice = values.slice(lo, i + 1);
    out.push(slice.reduce((a, b) => a + b, 0) / slice.length);
  }
  return out;
}

export async function loadAriaResults(): Promise<AriaResults> {
  const [scorecard, dashboard, baseServed, ariaServed, baseC, ariaC, lossRun] = await Promise.all([
    getJSON<RawScorecard>('scorecard.json'),
    getJSON<RawDashboard>('dashboard_results.json'),
    getJSON<RawEval>('_eval/base-served.json'),
    getJSON<RawEval>('_eval/aria-served.json'),
    getJSON<RawEval>('_eval/base-c.json'),
    getJSON<RawEval>('_eval/aria-c.json'),
    getJSON<RawRun>(ARIA_LOSS_RUN),
  ]);

  const baseVals = scorecard.models.base ?? {};
  const ariaVals = pick(scorecard.models, ARIA_SCORECARD_PRIORITY) ?? {};

  // ── Scorecard bars (authoritative aggregate %, matches the deck) ──
  const scoreBars: DimScore[] = DIMS.map(({ key, tag }) => ({
    key,
    base: round(baseVals[tag] ?? 0),
    aria: round(ariaVals[tag] ?? 0),
  }));

  // ── Fingerprint radar = ALL five scorecard dims + Citation-Coverage ──
  // Plots every evaluated dimension (incl. the weakest, E1 & F) so the
  // fingerprint is an honest whole-model picture, not a cherry-picked subset.
  const baseModel = dashboard.models.find((m) => m.name === 'base');
  const ariaModel =
    ARIA_DASHBOARD_PRIORITY.map((n) => dashboard.models.find((m) => m.name === n)).find(Boolean) ??
    dashboard.models[dashboard.models.length - 1];

  const fingerprint: RadarAxis[] = FINGERPRINT_DIMS.map(({ key, tag }) => ({
    key,
    base: round(baseVals[tag] ?? 0),
    aria: round(ariaVals[tag] ?? 0),
  }));
  fingerprint.push({
    key: 'coverage',
    base: round(baseModel?.values.coverage ?? 0),
    aria: round(ariaModel?.values.coverage ?? 0),
  });

  // ── Eval grid (per-case dots, matches the deck's Eval-Grid slide) ──
  const evalGrid: EvalGridDim[] = DIMS.map(({ key, tag }) => {
    const base = casesFor(tag, baseServed, baseC);
    const aria = casesFor(tag, ariaServed, ariaC);
    return {
      key,
      base,
      aria,
      basePass: base.filter(Boolean).length,
      ariaPass: aria.filter(Boolean).length,
      total: aria.length || base.length,
    };
  });

  // ── Loss curve (raw + smoothed) ──
  const series = lossRun.loss_series ?? [];
  const smooth = movingAverage(series);
  const loss: LossSeries = {
    run: lossRun.name,
    points: series.map((v, i) => ({ step: i + 1, loss: v, smooth: smooth[i] })),
    finalLoss: lossRun.loss ?? series[series.length - 1] ?? 0,
  };

  return { scorecard: scoreBars, fingerprint, evalGrid, loss };
}
